import { useCallback, useEffect, useReducer, useRef } from "react";
import toast from "react-hot-toast";
import {
  fetchWorkspace,
  getAdminUser,
  hasPermission,
  saveWorkspaceOnboarding,
  suggestWorkspaceOnboarding,
} from "../api/adminClient";
import OnboardingSuggestionPanel from "./OnboardingSuggestionPanel";
import TenantOnboardingForm from "./TenantOnboardingForm";
import {
  FORM_TO_SUGGESTION,
  emptyOnboardingForm,
  formFromWorkspace,
  onboardingFormSchema,
  onboardingSuggestSchema,
  readFormField,
  snapshotForm,
  suggestionStateFromApi,
  suggestErrorsToForm,
  toOnboardingApiPayload,
  toSuggestInput,
  writeFormField,
  zodFieldErrors,
} from "./onboardingConfigSchema";

function canConfigureOnboarding(user) {
  return Boolean(user?.tenant_id) && !user?.is_platform_admin && hasPermission("admin:manage_users");
}

const initialState = {
  loading: true,
  ready: false,
  saving: false,
  generating: false,
  form: emptyOnboardingForm(),
  baseline: snapshotForm(emptyOnboardingForm()),
  productName: "",
  brief: "",
  suggestion: null,
  fieldErrors: {},
};

function reducer(state, action) {
  switch (action.type) {
    case "loaded":
      return {
        ...state,
        loading: false,
        ready: true,
        saving: false,
        generating: false,
        form: action.form,
        baseline: snapshotForm(action.form),
        productName: action.productName || "",
        suggestion: null,
        fieldErrors: {},
      };
    case "load_failed":
      return { ...state, loading: false, ready: false };
    case "patch_form": {
      const form = { ...state.form, ...action.patch };
      const fieldErrors = { ...state.fieldErrors };
      Object.keys(action.patch).forEach((key) => {
        delete fieldErrors[key];
      });
      let suggestion = state.suggestion;
      if (suggestion) {
        const fields = { ...suggestion.fields };
        let changed = false;
        Object.entries(action.patch).forEach(([formKey, value]) => {
          const suggestionKey = FORM_TO_SUGGESTION[formKey];
          if (!suggestionKey || !fields[suggestionKey] || fields[suggestionKey].decision !== "accept") return;
          fields[suggestionKey] = { ...fields[suggestionKey], value };
          changed = true;
        });
        if (changed) suggestion = { ...suggestion, fields };
      }
      return { ...state, form, fieldErrors, suggestion };
    }
    case "set_brief": {
      const fieldErrors = { ...state.fieldErrors };
      delete fieldErrors.brief;
      return { ...state, brief: action.brief, fieldErrors };
    }
    case "field_errors":
      return { ...state, fieldErrors: action.errors };
    case "generating":
      return { ...state, generating: action.value };
    case "suggestion":
      return { ...state, generating: false, suggestion: action.suggestion };
    case "edit_suggestion": {
      const current = state.suggestion?.fields?.[action.key];
      if (!current) return state;
      const fields = {
        ...state.suggestion.fields,
        [action.key]: { ...current, value: action.value },
      };
      const form =
        current.decision === "accept" ? writeFormField(state.form, action.key, action.value) : state.form;
      return { ...state, form, suggestion: { ...state.suggestion, fields } };
    }
    case "decide": {
      const current = state.suggestion?.fields?.[action.key];
      if (!current) return state;
      const fields = { ...state.suggestion.fields };
      let form = state.form;
      if (action.decision === "accept") {
        const previous =
          current.decision === "accept" ? current.previous : String(readFormField(form, action.key) ?? "");
        fields[action.key] = { ...current, decision: "accept", previous };
        form = writeFormField(form, action.key, current.value);
      } else {
        fields[action.key] = { ...current, decision: "reject" };
        if (current.decision === "accept" && current.previous != null) {
          form = writeFormField(form, action.key, current.previous);
        }
      }
      return { ...state, form, suggestion: { ...state.suggestion, fields } };
    }
    case "dismiss_suggestion":
      return { ...state, suggestion: null };
    case "saving":
      return { ...state, saving: action.value };
    case "saved":
      return {
        ...state,
        saving: false,
        form: action.form,
        baseline: snapshotForm(action.form),
        productName: action.productName ?? state.productName,
        suggestion: null,
        fieldErrors: {},
      };
    default:
      return state;
  }
}

export default function TenantOnboardingAssistant({ onSaved }) {
  const session = getAdminUser();
  const allowed = canConfigureOnboarding(session);
  const [state, dispatch] = useReducer(reducer, initialState);
  const formRef = useRef(state.form);
  const baselineRef = useRef(state.baseline);
  const productRef = useRef(state.productName);
  const briefRef = useRef(state.brief);
  formRef.current = state.form;
  baselineRef.current = state.baseline;
  productRef.current = state.productName;
  briefRef.current = state.brief;

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWorkspace();
        if (cancelled) return;
        const workspace = res.data?.workspace;
        if (!workspace) {
          dispatch({ type: "load_failed" });
          return;
        }
        dispatch({
          type: "loaded",
          form: formFromWorkspace(workspace),
          productName: workspace.product_display_name || workspace.name || "",
        });
      } catch {
        if (!cancelled) dispatch({ type: "load_failed" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  const patchForm = useCallback((patch) => {
    dispatch({ type: "patch_form", patch });
  }, []);

  const onBriefChange = useCallback((brief) => {
    dispatch({ type: "set_brief", brief });
  }, []);

  const onEdit = useCallback((key, value) => {
    dispatch({ type: "edit_suggestion", key, value });
  }, []);

  const onDecide = useCallback((key, decision) => {
    dispatch({ type: "decide", key, decision });
  }, []);

  const onDismiss = useCallback(() => {
    dispatch({ type: "dismiss_suggestion" });
  }, []);

  const save = useCallback(async () => {
    let payloadSource = formRef.current;
    try {
      const latest = await fetchWorkspace();
      const workspace = latest.data?.workspace;
      if (workspace) {
        const serverForm = formFromWorkspace(workspace);
        const base = JSON.parse(baselineRef.current);
        payloadSource = { ...serverForm };
        Object.keys(formRef.current).forEach((key) => {
          if (formRef.current[key] !== base[key]) payloadSource[key] = formRef.current[key];
        });
      }
    } catch {
      payloadSource = formRef.current;
    }
    const parsed = onboardingFormSchema.safeParse(payloadSource);
    if (!parsed.success) {
      dispatch({ type: "field_errors", errors: zodFieldErrors(parsed.error) });
      toast.error("Check the highlighted onboarding fields");
      return;
    }
    dispatch({ type: "saving", value: true });
    try {
      const res = await saveWorkspaceOnboarding(toOnboardingApiPayload(parsed.data));
      const workspace = res.data?.workspace;
      const next = workspace ? formFromWorkspace(workspace) : parsed.data;
      dispatch({
        type: "saved",
        form: next,
        productName: workspace?.product_display_name || productRef.current,
      });
      toast.success("Onboarding settings saved");
      onSaved?.();
    } catch (err) {
      dispatch({ type: "saving", value: false });
      toast.error(err.message || "Could not save onboarding settings");
    }
  }, [onSaved]);

  const onGenerate = useCallback(async () => {
    const parsed = onboardingSuggestSchema.safeParse(
      toSuggestInput(formRef.current, briefRef.current, productRef.current)
    );
    if (!parsed.success) {
      dispatch({ type: "field_errors", errors: suggestErrorsToForm(parsed.error) });
      toast.error(parsed.error.issues[0]?.message || "Add an industry before generating");
      return;
    }
    dispatch({ type: "generating", value: true });
    try {
      const res = await suggestWorkspaceOnboarding(parsed.data);
      if (res.data?.persisted) {
        dispatch({ type: "generating", value: false });
        toast.error("Suggestions were saved unexpectedly. Refresh before editing.");
        return;
      }
      const suggestion = suggestionStateFromApi(res.data?.suggestion, formRef.current);
      if (!Object.keys(suggestion.fields).length) {
        dispatch({ type: "generating", value: false });
        toast.success("Suggestions already match this form");
        return;
      }
      dispatch({ type: "suggestion", suggestion });
    } catch (err) {
      dispatch({ type: "generating", value: false });
      toast.error(err.message || "Could not generate suggestions");
    }
  }, []);

  if (!allowed) return null;
  if (state.loading) {
    return (
      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500">
        Loading onboarding settings…
      </section>
    );
  }
  if (!state.ready) {
    return (
      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500">
        Onboarding settings could not be loaded.
      </section>
    );
  }

  const suggestionFields = state.suggestion?.fields || {};
  const decisions = Object.values(suggestionFields);
  const pendingCount = decisions.filter((field) => field.decision === "pending").length;
  const acceptedCount = decisions.filter((field) => field.decision === "accept").length;
  const dirty = snapshotForm(state.form) !== state.baseline;

  return (
    <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" aria-labelledby="tenant-onboarding-heading">
      <div className="mb-4">
        <h2 id="tenant-onboarding-heading" className="text-base font-semibold text-slate-900">
          Tenant onboarding
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">
          Set the logo, colors, welcome message, LITE or Full mode, and organization defaults. AI suggestions are saved only after you review them and confirm.
        </p>
      </div>
      <TenantOnboardingForm
        form={state.form}
        fieldErrors={state.fieldErrors}
        disabled={state.saving || state.generating}
        dirty={dirty}
        saveBlocked={pendingCount > 0}
        saving={state.saving}
        onPatch={patchForm}
        onSave={save}
      />
      <div className="mt-5">
        <OnboardingSuggestionPanel
          brief={state.brief}
          briefError={state.fieldErrors.brief || ""}
          suggestion={state.suggestion}
          generating={state.generating}
          disabled={state.saving}
          pendingCount={pendingCount}
          acceptedCount={acceptedCount}
          onBriefChange={onBriefChange}
          onGenerate={onGenerate}
          onEdit={onEdit}
          onDecide={onDecide}
          onDismiss={onDismiss}
          onSave={save}
        />
      </div>
    </section>
  );
}
